/**
 * WelcomeModal - First login greeting and What's New display
 *
 * Shows on:
 * - First login (welcome new users)
 * - New version (show what's new)
 *
 * Features:
 * - Personalized greeting
 * - Quick fleet stats (first login)
 * - What's new items grouped by category
 * - CTAs for tour and onboarding
 */

import React, { useState, useEffect } from 'react';
import {
  Sparkles, X, ArrowRight, HelpCircle, Settings,
  Star, Zap, Wrench, Shield, ChevronRight,
  Car, Users, AlertTriangle, CheckCircle,
  FileSpreadsheet, Layout, Save, Inbox, FileCheck, Upload, Search
} from 'lucide-react';
import { welcomeAPI, onboardingAPI } from '../../../api/index.js';

// Icon mapping for what's new items
const ICON_MAP = {
  Sparkles,
  HelpCircle,
  Save,
  FileSpreadsheet,
  Layout,
  Star,
  Zap,
  Wrench,
  Shield,
  CheckCircle,
  Car,
  Users,
  AlertTriangle,
  Settings,
  Inbox,
  FileCheck,
  Upload,
  Search,
};

// Color mapping for categories
const CATEGORY_COLORS = {
  feature: { bg: 'bg-amber-100', text: 'text-amber-800', icon: 'text-amber-600' },
  improvement: { bg: 'bg-blue-100', text: 'text-blue-800', icon: 'text-blue-600' },
  fix: { bg: 'bg-green-100', text: 'text-green-800', icon: 'text-green-600' },
  security: { bg: 'bg-red-100', text: 'text-red-800', icon: 'text-red-600' },
};

const CATEGORY_LABELS = {
  feature: 'Nouveautés',
  improvement: 'Améliorations',
  fix: 'Corrections',
  security: 'Sécurité',
};

function WelcomeModal({ user, onClose, onStartTour, onStartOnboarding, onNavigate }) {
  const [whatsNew, setWhatsNew] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');

  // Load what's new and completeness data
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const hasOrgContext = !!(user.orgId || user.org_id);

    const loadData = async () => {
      try {
        // Check welcome state — does not require org context
        const checkResponse = await welcomeAPI.check();
        if (checkResponse.ok) {
          setIsFirstLogin(checkResponse.data.isFirstLogin);
          setCurrentVersion(checkResponse.data.currentVersion);
        }

        // Load what's new — does not require org context
        const whatsNewResponse = await welcomeAPI.getWhatsNew();
        if (whatsNewResponse.ok) {
          setWhatsNew(whatsNewResponse.data);
        }

        // Load completeness for first login — this one needs org context
        if (checkResponse.data?.isFirstLogin && hasOrgContext) {
          const completenessResponse = await onboardingAPI.getCompleteness();
          if (completenessResponse.ok) {
            // Transform sections data to flat structure for display
            const data = completenessResponse.data;
            const sections = data.sections || [];
            const vehiclesSection = sections.find(s => s.key === 'vehicles');
            const driversSection = sections.find(s => s.key === 'drivers');

            // Calculate percentage from sections if overall is 0
            let percentage = data.overall || 0;
            if (percentage === 0 && sections.length > 0) {
              const totalWeight = sections.reduce((sum, s) => sum + (s.weight || 0), 0);
              const completedWeight = sections.reduce((sum, s) => sum + (s.complete ? (s.weight || 0) : 0), 0);
              percentage = totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;
            }

            setCompleteness({
              vehicles: vehiclesSection?.count || 0,
              drivers: driversSection?.count || 0,
              percentage: percentage,
              sections: sections
            });
          }
        }
      } catch (err) {
        console.error('[WelcomeModal] Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Handle dismiss
  const handleClose = async () => {
    try {
      await welcomeAPI.dismiss();
    } catch (err) {
      console.error('[WelcomeModal] Error dismissing:', err);
    }
    onClose?.();
  };

  // ux-whats-new-empty-modal-kill-it: auto-dismiss if the modal ends up empty
  // (backend should already prevent this, but guard covers races / future regressions).
  useEffect(() => {
    if (loading) return;
    if (isFirstLogin) return;
    if (whatsNew && !(whatsNew.items?.length > 0)) {
      handleClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isFirstLogin, whatsNew]);

  // Handle start tour
  const handleStartTour = async () => {
    await handleClose();
    onStartTour?.();
  };

  // Handle start onboarding
  const handleStartOnboarding = async () => {
    await handleClose();
    onStartOnboarding?.();
  };

  // Handle item click to navigate
  const handleItemClick = async (item) => {
    if (item.link_view && onNavigate) {
      await handleClose();
      onNavigate(item.link_view);
    }
  };

  // Get user's first name
  const firstName = user?.first_name || user?.firstName || 'utilisateur';

  // Render what's new item
  const renderWhatsNewItem = (item) => {
    const IconComponent = ICON_MAP[item.icon] || Sparkles;
    const colors = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.feature;
    const isClickable = item.link_view && onNavigate;

    return (
      <div
        key={item.id}
        onClick={isClickable ? () => handleItemClick(item) : undefined}
        className={`flex items-start gap-3 p-3 rounded-lg ${
          item.is_highlight ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
        } ${isClickable ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
      >
        <div className={`p-2 rounded-lg ${colors.bg}`}>
          <IconComponent className={`w-4 h-4 ${colors.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            {item.title}
            {item.is_highlight && (
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            )}
          </h4>
          {item.description && (
            <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
          )}
        </div>
        {item.link_view && (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-amber-500 via-amber-400 to-orange-400 px-6 py-8 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {isFirstLogin ? `Bienvenue, ${firstName}!` : `Quoi de neuf?`}
              </h2>
              <p className="text-amber-100">
                {isFirstLogin
                  ? 'Découvrez FleetManager'
                  : `Version ${currentVersion}`}
              </p>
            </div>
          </div>

          {/* Quick stats for first login */}
          {isFirstLogin && completeness && (
            <div className="flex gap-4 mt-4">
              <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{completeness.vehicles || 0}</div>
                <div className="text-xs text-amber-100">Véhicules</div>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{completeness.drivers || 0}</div>
                <div className="text-xs text-amber-100">Conducteurs</div>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{Math.round(completeness.percentage || 0)}%</div>
                <div className="text-xs text-amber-100">Complet</div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <>
              {/* What's New */}
              {whatsNew?.items?.length > 0 && (
                <div className="space-y-4">
                  {/* Highlights first */}
                  {whatsNew.items.filter(i => i.is_highlight).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Points forts
                      </h3>
                      <div className="space-y-2">
                        {whatsNew.items.filter(i => i.is_highlight).map(renderWhatsNewItem)}
                      </div>
                    </div>
                  )}

                  {/* Other items by category */}
                  {Object.entries(whatsNew.grouped || {}).map(([category, items]) => {
                    const nonHighlightItems = items.filter(i => !i.is_highlight);
                    if (nonHighlightItems.length === 0) return null;

                    return (
                      <div key={category}>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          {CATEGORY_LABELS[category] || category}
                        </h3>
                        <div className="space-y-2">
                          {nonHighlightItems.map(renderWhatsNewItem)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {!whatsNew?.items?.length && !isFirstLogin && (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucune nouveauté pour cette version.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with CTAs */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Start Tour */}
            <button
              onClick={handleStartTour}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Visite guidée</span>
            </button>

            {/* Configure Fleet (first login only) */}
            {isFirstLogin && completeness?.percentage < 100 && (
              <button
                onClick={handleStartOnboarding}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Configurer ma flotte</span>
              </button>
            )}

            {/* Close */}
            <button
              onClick={handleClose}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              <span>Plus tard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeModal;
